<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <router-link :to="{ name: ACCOUNTS_ROUTE }" class="back-link">
            <i class="pi pi-arrow-left"></i> Back to accounts
          </router-link>

          <!-- Loading the account -->
          <div v-if="isLoading" class="detail-loading">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading account…</span>
          </div>

          <!-- Not found -->
          <div v-else-if="!account" class="list-page__empty">
            <i class="pi pi-user"></i>
            <div>This account could not be found.</div>
          </div>

          <template v-else>
            <!-- Header -->
            <header class="detail-header">
              <div class="detail-id">
                <span class="detail-avatar">{{ initials }}</span>
                <div class="detail-id-text">
                  <h1>{{ fullName || '—' }}</h1>
                  <p class="detail-sub">{{ account.email || account.phoneNumber || '—' }}</p>
                  <div class="detail-badges">
                    <span class="badge" :class="roleBadgeClass(account.role)">{{ roleLabel(account.role) }}</span>
                    <span v-if="account.isActive" class="badge badge-success">Active</span>
                    <span v-else class="badge badge-danger">Inactive</span>
                    <span class="detail-since">Member since {{ $filters.getDateOnly(account.createdAt) }}</span>
                  </div>
                </div>
              </div>
            </header>

            <!-- Tabs -->
            <nav class="detail-tabs" role="tablist">
              <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                role="tab"
                :aria-selected="activeTab === tab.key"
                :class="['detail-tab', { 'is-active': activeTab === tab.key }]"
                v-on:click="setTab(tab.key)"
              >
                <i :class="['pi', tab.icon]"></i>
                <span>{{ tab.label }}</span>
              </button>
            </nav>

            <!-- Overview -->
            <section v-if="activeTab === 'overview'" class="panel">
              <div class="info-grid">
                <div class="info-cell">
                  <span class="info-label">Full name</span>
                  <span class="info-value">{{ fullName || '—' }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Email</span>
                  <span class="info-value">{{ account.email || '—' }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Phone</span>
                  <span class="info-value">{{ account.phoneNumber || '—' }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Role</span>
                  <span class="info-value"><span class="badge" :class="roleBadgeClass(account.role)">{{ roleLabel(account.role) }}</span></span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Status</span>
                  <span class="info-value">
                    <span v-if="account.isActive" class="badge badge-success">Active</span>
                    <span v-else class="badge badge-danger">Inactive</span>
                  </span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Email verified</span>
                  <span class="info-value">{{ account.isEmailVerified ? 'Yes' : 'No' }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Organization</span>
                  <span class="info-value">{{ organizationName || '—' }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Subscription</span>
                  <span class="info-value">{{ planName || 'No subscription' }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-label">Joined</span>
                  <span class="info-value">{{ $filters.getDateOnly(account.createdAt) }}</span>
                </div>
              </div>
            </section>

            <!-- Verifications -->
            <section v-else-if="activeTab === 'verifications'" class="tab-table">
              <div class="list-page__table">
                <div v-if="!account.organizationId" class="list-page__empty">
                  <i class="pi pi-inbox"></i>
                  <div>This account has no organization, so it has no verifications.</div>
                </div>
                <template v-else>
                  <tableLoader v-if="verLoading" message="Loading verifications…" />
                  <div v-if="!verLoading" class="table-scroller">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Bank</th>
                          <th scope="col">Amount</th>
                          <th scope="col">Reference</th>
                          <th scope="col">Status</th>
                          <th scope="col">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(v, index) in verifications" v-bind:key="v.id || index">
                          <th scope="row">{{ verRowNumber(index) }}</th>
                          <td>{{ bankLabel(v.bank) }}</td>
                          <td class="row-name">{{ money(v.amount, v.currency) }}</td>
                          <td><span class="ref-text">{{ v.transactionReference || v.transactionId || '—' }}</span></td>
                          <td><span class="badge" :class="statusBadgeClass(v.status)">{{ statusLabel(v.status) }}</span></td>
                          <td>{{ $filters.getDateOnly(v.date || v.createdAt) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="!verLoading && verifications.length === 0" class="list-page__empty">
                    <i class="pi pi-inbox"></i>
                    <div>No verifications for this account yet.</div>
                  </div>
                </template>
              </div>
              <pagination
                v-if="account.organizationId && !verLoading && verMetaData"
                :meta-data="verMetaData"
                :current-page="verPage"
                :per-page="verPerPage"
                v-on:change="onVerPageChange"
              />
            </section>

            <!-- Bank Accounts -->
            <section v-else-if="activeTab === 'bank-accounts'" class="tab-table">
              <div class="list-page__table">
                <div v-if="!account.organizationId" class="list-page__empty">
                  <i class="pi pi-building-columns"></i>
                  <div>This account has no organization, so it has no bank accounts.</div>
                </div>
                <template v-else>
                  <tableLoader v-if="bankLoading" message="Loading bank accounts…" />
                  <div v-if="!bankLoading" class="table-scroller">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Name</th>
                          <th scope="col">Bank</th>
                          <th scope="col">Account name</th>
                          <th scope="col">Account number</th>
                          <th scope="col">Status</th>
                          <th scope="col">Added</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(bankAccount, index) in bankAccounts" v-bind:key="bankAccount.id || index">
                          <th scope="row">{{ bankRowNumber(index) }}</th>
                          <td class="row-name">{{ bankAccount.name || '—' }}</td>
                          <td>{{ bankLabel(bankAccount.bank) }}</td>
                          <td>{{ bankAccount.accountName || '—' }}</td>
                          <td><span class="ref-text">{{ bankAccount.accountNumber || '—' }}</span></td>
                          <td>
                            <span v-if="bankAccount.isActive" class="badge badge-success">Active</span>
                            <span v-else class="badge badge-danger">Inactive</span>
                          </td>
                          <td>{{ $filters.getDateOnly(bankAccount.createdAt) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="!bankLoading && bankAccounts.length === 0" class="list-page__empty">
                    <i class="pi pi-building-columns"></i>
                    <div>No bank accounts for this account yet.</div>
                  </div>
                </template>
              </div>
              <pagination
                v-if="account.organizationId && !bankLoading && bankMetaData"
                :meta-data="bankMetaData"
                :current-page="bankPage"
                :per-page="bankPerPage"
                v-on:change="onBankPageChange"
              />
            </section>

            <!-- Subscription -->
            <section v-else-if="activeTab === 'subscription'" class="panel">
              <div v-if="!account.organizationId" class="list-page__empty">
                <i class="pi pi-credit-card"></i>
                <div>This account has no organization, so it has no subscription.</div>
              </div>
              <template v-else>
                <tableLoader v-if="subLoading" message="Loading subscription…" />
                <template v-else>
                  <div v-if="currentSubscription" class="sub-card">
                    <div class="sub-card-head">
                      <div>
                        <span class="sub-eyebrow"><i class="pi pi-crown"></i> Current plan</span>
                        <h3 class="sub-plan-name">{{ subPlan ? subPlan.name : 'Unknown plan' }}</h3>
                      </div>
                      <span class="badge" :class="subStatus.badgeCls">{{ subStatus.label }}</span>
                    </div>
                    <div class="sub-meta-grid">
                      <div class="info-cell">
                        <span class="info-label">Price</span>
                        <span class="info-value">{{ subPlan ? money(subPlan.totalPrice ?? subPlan.price, 'ETB') : '—' }}</span>
                      </div>
                      <div class="info-cell">
                        <span class="info-label">Duration</span>
                        <span class="info-value">{{ subPlan && subPlan.duration ? subPlan.duration + ' days' : '—' }}</span>
                      </div>
                      <div class="info-cell">
                        <span class="info-label">Verification limit</span>
                        <span class="info-value">{{ verificationLimitLabel }}</span>
                      </div>
                      <div class="info-cell">
                        <span class="info-label">Team seats</span>
                        <span class="info-value">{{ subPlan && subPlan.maxUsers != null ? subPlan.maxUsers : 'Unlimited' }}</span>
                      </div>
                      <div class="info-cell">
                        <span class="info-label">Started</span>
                        <span class="info-value">{{ $filters.getDateOnly(currentSubscription.startDate) }}</span>
                      </div>
                      <div class="info-cell">
                        <span class="info-label">Renews / expires</span>
                        <span class="info-value">{{ $filters.getDateOnly(currentSubscription.endDate) }}</span>
                      </div>
                    </div>
                    <ul v-if="subPlan && subPlan.features && subPlan.features.length" class="sub-features">
                      <li v-for="(feature, i) in subPlan.features" :key="i">
                        <i class="pi pi-check"></i> {{ feature }}
                      </li>
                    </ul>
                  </div>
                  <div v-else class="list-page__empty">
                    <i class="pi pi-credit-card"></i>
                    <div>This account has no active subscription.</div>
                  </div>

                  <!-- History -->
                  <div v-if="pastSubscriptions.length" class="sub-history">
                    <h4 class="sub-history-title">History</h4>
                    <div class="list-page__table">
                      <div class="table-scroller">
                        <table>
                          <thead>
                            <tr>
                              <th scope="col">Plan</th>
                              <th scope="col">Started</th>
                              <th scope="col">Ended</th>
                              <th scope="col">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="sub in pastSubscriptions" v-bind:key="sub.id">
                              <td class="row-name">{{ sub.subscriptionPlan ? sub.subscriptionPlan.name : '—' }}</td>
                              <td>{{ $filters.getDateOnly(sub.startDate) }}</td>
                              <td>{{ $filters.getDateOnly(sub.endDate) }}</td>
                              <td>
                                <span v-if="sub.isActive" class="badge badge-success">Active</span>
                                <span v-else class="badge badge-secondary">Ended</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </section>

            <!-- Settings -->
            <section v-else-if="activeTab === 'settings'" class="panel">
              <form class="settings-form" v-on:submit.prevent="saveSettings">
                <h4 class="settings-title">Account details</h4>
                <div class="settings-grid">
                  <div class="form-group">
                    <label>First name</label>
                    <input v-model.trim="form.fname" type="text" class="form-control shadow-none" placeholder="First name" />
                  </div>
                  <div class="form-group">
                    <label>Last name</label>
                    <input v-model.trim="form.lname" type="text" class="form-control shadow-none" placeholder="Last name" />
                  </div>
                  <div class="form-group">
                    <label>Email</label>
                    <input v-model.trim="form.email" type="email" class="form-control shadow-none" placeholder="Email" />
                  </div>
                  <div class="form-group">
                    <label>Phone number</label>
                    <input v-model.trim="form.phoneNumber" type="tel" class="form-control shadow-none" placeholder="Phone number" />
                  </div>
                  <div class="form-group">
                    <label>Role</label>
                    <select v-model="form.role" class="form-control shadow-none">
                      <option v-for="role in roleOptions" :key="role" :value="role">{{ roleLabel(role) }}</option>
                    </select>
                    <small class="form-hint">Only admin / super-admin roles can sign in to this platform.</small>
                  </div>
                </div>
                <div class="settings-actions">
                  <button type="submit" class="btn btn-custom-primary" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span v-else>Save changes</span>
                  </button>
                </div>
              </form>

              <div class="danger-zone">
                <div class="danger-row">
                  <div class="danger-text">
                    <h5>{{ account.isActive ? 'Deactivate account' : 'Activate account' }}</h5>
                    <p>{{ account.isActive ? 'A deactivated account cannot sign in until reactivated.' : 'Reactivate this account so the user can sign in again.' }}</p>
                  </div>
                  <button
                    type="button"
                    class="btn"
                    :class="account.isActive ? 'btn-custom-secondary' : 'btn-custom-primary'"
                    :disabled="togglingActive"
                    v-on:click="toggleActive"
                  >
                    <span v-if="togglingActive" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span v-else>{{ account.isActive ? 'Deactivate' : 'Activate' }}</span>
                  </button>
                </div>
                <div class="danger-row danger-row--delete">
                  <div class="danger-text">
                    <h5>Delete account</h5>
                    <p>Soft-deletes this account. It will no longer appear in the accounts list.</p>
                  </div>
                  <button type="button" class="btn btn-danger-outline" v-on:click="showDeleteModal = true">
                    Delete
                  </button>
                </div>
              </div>
            </section>
          </template>
        </div>
      </div>
    </div>

    <deleteConfirmationModal
      v-if="showDeleteModal"
      :delete-function="deleteAccount"
      title="Delete account"
      :message="`Are you sure you want to delete this account?`"
      :record-label="account ? (fullName || account.email || account.id) : ''"
      @close-delete-confirmation-modal="showDeleteModal = false"
      @refresh-records="onAccountDeleted"
    />
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import tableLoader from "@/components/Table_Loader.vue";
import pagination from "@/components/Pagination.vue";
import deleteConfirmationModal from "@/components/Delete_Confirmation_Modal.vue";
import Account from "@/services/account";
import Verification from "@/services/verification";
import Subscription from "@/services/subscription";
import BankAccount from "@/services/recipient_account";
import { BANK_LABELS, ROLE_LABELS, USER_ROLES, VERIFICATION_STATUS } from "@/utils/enums";
import { DEFAULT_CURRENCY, TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { ACCOUNTS_ROUTE } from "@/router/routes";

const ROLE_BADGE = {
  [USER_ROLES.superAdmin]: "badge-info",
  [USER_ROLES.admin]: "badge-info",
  [USER_ROLES.owner]: "badge-warning",
  [USER_ROLES.user]: "badge-secondary",
};

const STATUS_BADGE = {
  [VERIFICATION_STATUS.verified]: "badge-success",
  [VERIFICATION_STATUS.pending]: "badge-warning",
  [VERIFICATION_STATUS.rejected]: "badge-danger",
  [VERIFICATION_STATUS.failed]: "badge-danger",
};

export default {
  title: "Account",
  components: {
    sidenav,
    navbar,
    tableLoader,
    pagination,
    deleteConfirmationModal,
  },
  data() {
    return {
      ACCOUNTS_ROUTE,
      id: this.$route.params.id,
      account: null,
      isLoading: true,
      activeTab: "overview",
      tabs: [
        { key: "overview", label: "Overview", icon: "pi-id-card" },
        { key: "verifications", label: "Verifications", icon: "pi-check-circle" },
        { key: "bank-accounts", label: "Bank Accounts", icon: "pi-building-columns" },
        { key: "subscription", label: "Subscription", icon: "pi-credit-card" },
        { key: "settings", label: "Settings", icon: "pi-cog" },
      ],
      // Verifications tab (lazy-loaded)
      verifications: [],
      verLoading: false,
      verLoaded: false,
      verMetaData: null,
      verPage: 1,
      verPerPage: 30,
      // Bank accounts tab (lazy-loaded)
      bankAccounts: [],
      bankLoading: false,
      bankLoaded: false,
      bankMetaData: null,
      bankPage: 1,
      bankPerPage: 30,
      // Subscription tab (lazy-loaded)
      subscriptions: [],
      subLoading: false,
      subLoaded: false,
      // Settings tab
      form: { fname: "", lname: "", email: "", phoneNumber: "", role: USER_ROLES.user },
      saving: false,
      togglingActive: false,
      showDeleteModal: false,
      roleOptions: [USER_ROLES.superAdmin, USER_ROLES.admin, USER_ROLES.owner, USER_ROLES.user],
    };
  },
  computed: {
    fullName() {
      return this.account ? this.$filters.fullName(this.account.fname, this.account.lname) : "";
    },
    initials() {
      if (!this.account) return "?";
      const f = (this.account.fname || "").charAt(0);
      const l = (this.account.lname || "").charAt(0);
      return (f + l).toUpperCase() || "?";
    },
    organizationName() {
      return this.account && this.account.organization ? this.account.organization.name : "";
    },
    // The active subscription: prefer the freshly loaded list, fall back to the
    // one embedded on the account's organization.
    currentSubscription() {
      const fromList = this.subscriptions.find((s) => s.isActive && !s.isPending);
      if (fromList) return fromList;
      const embedded = this.account && this.account.organization ? this.account.organization.subscription : null;
      return embedded || null;
    },
    subPlan() {
      return this.currentSubscription ? this.currentSubscription.subscriptionPlan : null;
    },
    planName() {
      return this.subPlan ? this.subPlan.name : "";
    },
    pastSubscriptions() {
      const currentId = this.currentSubscription ? this.currentSubscription.id : null;
      return this.subscriptions.filter((s) => s.id !== currentId);
    },
    subStatus() {
      const sub = this.currentSubscription;
      if (!sub) return { label: "Inactive", badgeCls: "badge-secondary" };
      const ended = sub.endDate && new Date(sub.endDate).getTime() < Date.now();
      if (ended) return { label: "Expired", badgeCls: "badge-danger" };
      return { label: "Active", badgeCls: "badge-success" };
    },
    verificationLimitLabel() {
      if (!this.subPlan) return "—";
      if (this.subPlan.verificationLimit == null) return "Unlimited";
      const period = this.subPlan.limitPeriod === "total" ? "lifetime" : this.subPlan.limitPeriod;
      return `${this.subPlan.verificationLimit} / ${period}`;
    },
  },
  methods: {
    roleLabel(role) {
      return ROLE_LABELS[role] || role || "—";
    },
    roleBadgeClass(role) {
      return ROLE_BADGE[role] || "badge-secondary";
    },
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    statusLabel(status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
    },
    statusBadgeClass(status) {
      return STATUS_BADGE[status] || "badge-secondary";
    },
    money(value, currency) {
      const amount = Number(value);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, currency || DEFAULT_CURRENCY);
    },
    verRowNumber(index) {
      return (this.verPage - 1) * this.verPerPage + index + 1;
    },
    setTab(tab) {
      this.activeTab = tab;
      if (tab === "verifications" && !this.verLoaded) this.loadVerifications();
      if (tab === "bank-accounts" && !this.bankLoaded) this.loadBankAccounts();
      if (tab === "subscription" && !this.subLoaded) this.loadSubscriptions();
    },
    bankRowNumber(index) {
      return (this.bankPage - 1) * this.bankPerPage + index + 1;
    },
    populateForm() {
      if (!this.account) return;
      this.form = {
        fname: this.account.fname || "",
        lname: this.account.lname || "",
        email: this.account.email || "",
        phoneNumber: this.account.phoneNumber || "",
        role: this.account.role || USER_ROLES.user,
      };
    },
    async loadAccount() {
      this.isLoading = true;
      try {
        const res = await Account.getAccount(this.id);
        if (res?.data?.status) {
          const list = res.data.data || [];
          this.account = list.length ? list[0] : null;
          this.populateForm();
        }
      } finally {
        this.isLoading = false;
      }
    },
    async loadVerifications() {
      if (!this.account || !this.account.organizationId) return;
      this.verLoading = true;
      try {
        const res = await Verification.getVerifications({
          organizationId: this.account.organizationId,
          page: this.verPage,
          limit: this.verPerPage,
        });
        if (res?.data?.status) {
          this.verifications = res.data.data || [];
          this.verMetaData = res.data.metaData || null;
        }
      } finally {
        this.verLoading = false;
        this.verLoaded = true;
      }
    },
    onVerPageChange({ page, perPage }) {
      this.verPage = page;
      this.verPerPage = perPage;
      this.loadVerifications();
    },
    async loadBankAccounts() {
      if (!this.account || !this.account.organizationId) return;
      this.bankLoading = true;
      try {
        const res = await BankAccount.getBankAccounts({
          organizationId: this.account.organizationId,
          page: this.bankPage,
          limit: this.bankPerPage,
        });
        if (res?.data?.status) {
          this.bankAccounts = res.data.data || [];
          this.bankMetaData = res.data.metaData || null;
        }
      } finally {
        this.bankLoading = false;
        this.bankLoaded = true;
      }
    },
    onBankPageChange({ page, perPage }) {
      this.bankPage = page;
      this.bankPerPage = perPage;
      this.loadBankAccounts();
    },
    async loadSubscriptions() {
      if (!this.account || !this.account.organizationId) return;
      this.subLoading = true;
      try {
        const res = await Subscription.getSubscriptions({
          organizationId: this.account.organizationId,
          limit: 100,
        });
        if (res?.data?.status) {
          this.subscriptions = res.data.data || [];
        }
      } finally {
        this.subLoading = false;
        this.subLoaded = true;
      }
    },
    async saveSettings() {
      if (this.saving) return;
      this.saving = true;
      try {
        const res = await Account.updateAccount({
          id: this.account.id,
          fname: this.form.fname,
          lname: this.form.lname,
          email: this.form.email || undefined,
          phoneNumber: this.form.phoneNumber || undefined,
          role: this.form.role,
        });
        if (res?.data?.status) {
          this.$toast.success("Account updated.", TOAST_GLOBAL_CONFIG);
          await this.loadAccount();
        }
      } finally {
        this.saving = false;
      }
    },
    async toggleActive() {
      if (this.togglingActive) return;
      this.togglingActive = true;
      try {
        const res = await Account.toggleActive({ id: this.account.id });
        if (res?.data?.status) {
          this.$toast.success(
            this.account.isActive ? "Account deactivated." : "Account activated.",
            TOAST_GLOBAL_CONFIG,
          );
          await this.loadAccount();
        }
      } finally {
        this.togglingActive = false;
      }
    },
    // Passed to the confirmation modal; it resolves to the axios response on
    // success (truthy), which triggers the modal's refresh + close.
    deleteAccount() {
      return Account.deleteAccount(this.account.id);
    },
    onAccountDeleted() {
      this.$toast.success("Account deleted.", TOAST_GLOBAL_CONFIG);
      this.$router.push({ name: ACCOUNTS_ROUTE });
    },
  },
  mounted() {
    this.loadAccount();
  },
};
</script>
<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
  text-decoration: none;
}

.back-link:hover {
  color: var(--ehs-color-primary-active);
}

.back-link .pi {
  font-size: 0.72rem;
}

.detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  color: var(--ehs-color-muted);
}

.detail-loading .pi {
  font-size: 1.8rem;
  color: var(--ehs-color-soft);
}

/* Header */
.detail-header {
  padding: 20px 22px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  background: var(--ehs-color-surface);
  box-shadow: var(--ehs-shadow-card);
  margin-bottom: 16px;
}

.detail-id {
  display: flex;
  align-items: center;
  gap: 16px;
}

.detail-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--ehs-color-primary) 0%, #bcdc44 100%);
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 700;
}

.detail-id-text {
  min-width: 0;
}

.detail-id-text h1 {
  margin: 0;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.detail-sub {
  margin: 2px 0 8px;
  color: var(--ehs-color-muted);
  font-size: 0.85rem;
}

.detail-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.detail-since {
  font-size: 0.74rem;
  color: var(--ehs-color-soft);
}

/* Tabs */
.detail-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--ehs-color-border);
  margin-bottom: 18px;
  overflow-x: auto;
}

.detail-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 16px;
  border: 0;
  background: transparent;
  border-bottom: 2px solid transparent;
  color: var(--ehs-color-muted);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.detail-tab .pi {
  font-size: 0.85rem;
}

.detail-tab:hover {
  color: var(--ehs-color-ink);
}

.detail-tab.is-active {
  color: var(--ehs-color-primary-active);
  border-bottom-color: var(--ehs-color-primary);
}

/* Panels */
.panel {
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-lg);
  box-shadow: var(--ehs-shadow-card);
  padding: 22px;
}

/* Table tabs (verifications / bank accounts) render the table card on its own,
   exactly like the standalone list pages, with the pagination spaced below it. */
.tab-table {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-canvas);
}

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ehs-color-soft);
}

.info-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--ehs-color-ink);
  word-break: break-word;
}

.ref-text {
  font-family: var(--ehs-font-mono);
  font-size: 0.78rem;
  color: var(--ehs-color-text);
}

/* Subscription */
.sub-card {
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  padding: 20px;
  background:
    radial-gradient(120% 160% at 0% 0%, rgba(115, 196, 29, 0.08) 0%, rgba(115, 196, 29, 0) 55%),
    var(--ehs-color-canvas);
}

.sub-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.sub-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--ehs-color-muted);
}

.sub-eyebrow .pi {
  color: var(--ehs-color-primary);
}

.sub-plan-name {
  margin: 4px 0 0;
  font-family: var(--ehs-font-display);
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ehs-color-ink);
  text-transform: capitalize;
}

.sub-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.sub-features {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.sub-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: var(--ehs-color-text);
}

.sub-features .pi {
  color: var(--ehs-color-success);
  font-size: 0.75rem;
}

.sub-history {
  margin-top: 22px;
}

.sub-history-title {
  margin: 0 0 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
}

/* Settings */
.settings-title {
  margin: 0 0 16px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.settings-form .form-group {
  margin-bottom: 0;
}

.settings-form label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-text);
}

.form-hint {
  display: block;
  margin-top: 5px;
  font-size: 0.72rem;
  color: var(--ehs-color-soft);
}

.settings-actions {
  margin-top: 18px;
}

.danger-zone {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--ehs-color-border);
}

.danger-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
}

.danger-row--delete {
  border-top: 1px solid var(--ehs-color-border);
}

.danger-text h5 {
  margin: 0 0 3px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
}

.danger-text p {
  margin: 0;
  font-size: 0.78rem;
  color: var(--ehs-color-muted);
}

.btn-danger-outline {
  border: 1px solid var(--ehs-color-danger);
  color: var(--ehs-color-danger);
  background: var(--ehs-color-surface);
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.45rem 1rem;
  border-radius: var(--ehs-radius-md);
}

.btn-danger-outline:hover {
  background: var(--ehs-color-danger);
  color: #ffffff;
}

@media (max-width: 991.98px) {
  .info-grid,
  .sub-meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .settings-grid,
  .sub-features {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .info-grid,
  .sub-meta-grid {
    grid-template-columns: 1fr;
  }
  .danger-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
